import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  CreateMessage,
  MentionInput,
  Message,
  MessageInput,
  UpdateMessage,
} from '@skop-app/lib-domain/types'

import {
  createMentionMutation,
  createMessageMutation,
  createReviewMessageMutation,
  updateMessageMutation,
  updateReviewMessageMutation,
} from '@/services/graphql'
import { getAmplifyClient } from '@/services/amplifyClient'
import { useUserContext } from '@/context'
import {
  applyOptimisticReviewMessage,
  cancelReviewQueries,
  collectReviewMutationContext,
  restoreReviewMutationContext,
  revalidateReviewQueries,
  type ReviewMutationContext,
} from '@/services/mutations/reviewCache'

const client = getAmplifyClient()

type UpsertReviewMessageParams = {
  upsertType: 'create' | 'update' | 'delete'
  input: MessageInput & { productID?: string }
  mentions?: MentionInput[]
}

export const upsertReviewMessageRequest = async ({
  upsertType,
  input,
  mentions = [],
}: UpsertReviewMessageParams) => {
  const { productID: _productID, ...message } = input

  switch (upsertType) {
    case 'create':
      await client.graphql({
        query: createReviewMessageMutation,
        variables: { input: message },
      })
      break
    case 'update':
      await client.graphql({
        query: updateReviewMessageMutation,
        variables: { input: message },
      })
      break
    case 'delete':
      return
  }

  if (!mentions.length) return

  await Promise.all(
    mentions.map((mention) =>
      client.graphql({
        query: createMentionMutation,
        variables: {
          input: mention,
        },
      }),
    ),
  )
}

export const useCreateMessage = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['createMessage'],
    mutationFn: createMessage,
    onSuccess: (_data, variables) => {
      const { orderID } = variables.input
      const order = queryClient.getQueryData(['messagesByOrder', orderID]) as
        | Message[]
        | undefined

      if (!order) return

      const newOrder = { ...variables.input, ...variables.cacheData }
      queryClient.setQueryData(
        ['messagesByOrder', orderID],
        [...order, newOrder],
      )
    },
  })
}

export const createMessage = async ({ input }: CreateMessage) => {
  try {
    await client.graphql({
      query: createMessageMutation,
      variables: {
        input,
      },
    })
  } catch (error) {
    console.error(error)
  }
}

export const useUpdateMessage = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['updateMessage'],
    mutationFn: updateMessage,
    onMutate(variables) {
      const { orderID } = variables.cacheData
      const messages = queryClient.getQueryData([
        'messagesByOrder',
        orderID,
      ]) as Message[] | undefined

      if (!messages) return

      queryClient.setQueryData(
        ['messagesByOrder', orderID],
        messages.map((message) =>
          message.id === variables.input.id
            ? { ...message, ...variables.input }
            : message,
        ),
      )
    },
  })
}

const updateMessage = async ({ input }: UpdateMessage) => {
  try {
    await client.graphql({
      query: updateMessageMutation,
      variables: {
        input,
      },
    })
  } catch (error) {
    console.error(error)
  }
}

export const useUpsertReviewMessage = () => {
  const queryClient = useQueryClient()
  const { user } = useUserContext()

  return useMutation({
    mutationKey: ['upsertReviewMessage'],
    mutationFn: upsertReviewMessageRequest,
    onMutate: async ({ input: message, mentions }) => {
      if (!message.reviewID) {
        return {
          snapshots: [],
        } satisfies ReviewMutationContext
      }

      await cancelReviewQueries({
        queryClient,
        reviewId: message.reviewID,
        includeDetail: true,
        includeMessages: true,
        includeDrawer: true,
        includeGrid: true,
        drawerQueryMode: 'existingOnly',
      })

      const context = collectReviewMutationContext({
        queryClient,
        reviewId: message.reviewID,
        includeDetail: true,
        includeMessages: true,
        includeDrawer: true,
        includeGrid: true,
        drawerQueryMode: 'existingOnly',
      })

      applyOptimisticReviewMessage({
        queryClient,
        reviewId: message.reviewID,
        message,
        mentions,
        user,
      })

      return context
    },
    onError: (_error, _variables, context) => {
      restoreReviewMutationContext({ queryClient, context })
    },
    onSuccess: async (_data, variables) => {
      if (!variables.input.reviewID) return

      await revalidateReviewQueries({
        queryClient,
        reviewId: variables.input.reviewID,
        includeDetail: true,
        includeMessages: true,
        includeDrawer: true,
        includeGrid: true,
        includeNotifications: true,
        refetchDetail: true,
        drawerQueryMode: 'existingOnly',
      })
    },
  })
}
